import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useAmendmentCreateEditDispatch, useAmendmentCreateEditState } from './amendment-create-edit-context';
import AmendmentAppService from '../../../../application/platform/sge/amendment/amendment-app-service';
import ParlamentarianAppService from '../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../application/platform/sge/political-party/political-party-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetActionAppService from '../../../../application/platform/sge/budget-action/budget-action-app-service';
import { useBaseLayout } from '../../../../components/context/use-base-layout';

export const useAmendmentCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useAmendmentCreateEditDispatch();
    const state = useAmendmentCreateEditState();
    const { showMessage, showLoading } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        async function loadInitialData() {
            showLoading({ open: true });
            try {
                const [parlamentarians, politicalParties, budgetUnits] = await Promise.all([
                    ParlamentarianAppService.list({ page: 0, pageSize: 100 }),
                    PoliticalPartyAppService.list({ page: 0, pageSize: 100 }),
                    BudgetUnitAppService.list(),
                ]);
                dispatch({ type: ACTION_TYPES.setParlamentarians, payload: parlamentarians.items });
                dispatch({ type: ACTION_TYPES.setPoliticalParties, payload: politicalParties.items });
                dispatch({ type: ACTION_TYPES.setBudgetUnits, payload: budgetUnits });

                if (state.amendmentId) {
                    const data = await AmendmentAppService.getById(Number(state.amendmentId));
                    dispatch({ type: ACTION_TYPES.setAmendmentRequest, payload: data });
                    if (data.budgetActionId) {
                        const action = await BudgetActionAppService.getById(data.budgetActionId);
                        const program = await BudgetProgramAppService.getById(action.budgetProgramId);
                        dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: program.budgetUnitId });
                        dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: action.budgetProgramId });
                    }
                }
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível carregar os dados da emenda', detail: error?.details || error?.message, type: 'error' });
            } finally {
                showLoading({ open: false });
            }
        }
        loadInitialData();
    }, [state.amendmentId]);

    useEffect(() => {
        if (state.selectedBudgetUnitId) {
            BudgetProgramAppService.list({ budgetUnitId: state.selectedBudgetUnitId }).then((data) =>
                dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: data })
            );
        } else {
            dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: [] });
        }
    }, [state.selectedBudgetUnitId]);

    useEffect(() => {
        if (state.selectedBudgetProgramId) {
            BudgetActionAppService.list({ budgetProgramId: state.selectedBudgetProgramId }).then((data) =>
                dispatch({ type: ACTION_TYPES.setBudgetActions, payload: data })
            );
        } else {
            dispatch({ type: ACTION_TYPES.setBudgetActions, payload: [] });
        }
    }, [state.selectedBudgetProgramId]);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        let parsed: any = value;
        if (['year', 'parlamentarId', 'politicalPartyId', 'budgetActionId', 'type', 'status'].includes(name)) {
            parsed = value ? parseInt(value, 10) : undefined;
        }
        if (name === 'totalAmount') {
            parsed = value;
        }
        const payload: any = { [name]: parsed };
        if (name === 'parlamentarId') {
            const parlamentarian = state.parlamentarians.find((p) => p.id === parsed);
            payload.politicalPartyId = parlamentarian?.politicalPartyId;
        }
        dispatch({ type: ACTION_TYPES.updateAmendmentRequest, payload });
    }

    function handleBudgetUnitChange(e: any) {
        const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
        dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: value });
        dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: undefined });
        dispatch({ type: ACTION_TYPES.updateAmendmentRequest, payload: { budgetActionId: undefined } });
    }

    function handleBudgetProgramChange(e: any) {
        const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
        dispatch({ type: ACTION_TYPES.setSelectedBudgetProgramId, payload: value });
        dispatch({ type: ACTION_TYPES.updateAmendmentRequest, payload: { budgetActionId: undefined } });
    }

    function handleFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
        const fileList = e.target.files;
        if (fileList) {
            dispatch({ type: ACTION_TYPES.setFiles, payload: Array.from(fileList) });
        }
    }

    async function handleSave() {
        const req = state.amendmentRequest;
        if (
            !req.name || !req.year || !req.status || !req.type || !req.parlamentarId || !req.politicalPartyId ||
            !req.totalAmount || !state.selectedBudgetUnitId || !state.selectedBudgetProgramId || !req.budgetActionId ||
            !req.objectDescription
        ) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            return;
        }
        const payload = { ...state.amendmentRequest, files: state.files.length > 0 ? state.files : undefined };
        setIsSaving(true);
        try {
            let amendmentId = Number(state.amendmentId);
            if (state.amendmentId) {
                await AmendmentAppService.edit(amendmentId, payload);
                showMessage({ open: true, message: 'Emenda atualizada com sucesso!', type: 'success' });
            } else {
                const created = await AmendmentAppService.add(payload);
                amendmentId = created.id;
                showMessage({ open: true, message: 'Emenda adicionada com sucesso!', type: 'success' });
            }
            setTimeout(() => navigate(`/emendas/${amendmentId}`), 1500);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar a emenda', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleCancel() { navigate('/emendas'); }

    return { handleOnChange, handleBudgetUnitChange, handleBudgetProgramChange, handleFilesChange, handleSave, handleCancel, isSaving };
};
